import React, { useState, useEffect, useMemo } from 'react';
import MetricCard from './MetricCard';
import Utils from '../../../common/utils';
import Core from '../../../common/clientSdk';
import { EditIconBlue } from '../../../components/assets/icons';
import { ShimmerBox } from '../../../components';

// Shimmer loading component for metrics grid
const ShimmerLoading = () => (
    <div className="metrics-grid">
        {[...Array(15)].map((_, index) => (
            <div key={index} className="metric-card" style={{ padding: '16px', minHeight: '80px' }}>
                <ShimmerBox width="60%" height="14px" margin="0 0 12px 0" />
                <ShimmerBox width="80%" height="24px" />
            </div>
        ))}
    </div>
);

// Helper component to hide card if value is "-" (0 or less)
// Helper component to hide card if value is "-" (0 or less) ONLY for public/shared view
const RenderMetricCard = ({ value, isPublic, isSharedView, ...props }) => {
    // If public or shared view, hide empty/invalid values
    // If internal view (!isPublic && !isSharedView), show all cards even if "-"
    if ((isPublic || isSharedView) && (value === "-" || value === null || value === undefined)) return null;
    return <MetricCard value={value} {...props} />;
};

/**
 * OverviewSection - Statistics overview grid with metrics
 * @param {object} sectionRef - Ref for scroll navigation
 * @param {object} campaignContentAnalytic - Content analytics data
 * @param {object} campaignReport - Campaign report data
 * @param {object} userData - User data for currency conversion
 * @param {number} avgEngagement - Average engagement rate
 * @param {number} totalEngagement - Total engagement count
 * @param {number} cpEValue - Cost per engagement
 * @param {number} cpmValue - Cost per mille (views)
 * @param {function} onCustomizeClick - Callback for customize button
 */
const OverviewSection = ({
    sectionRef,
    campaignContentAnalytic,
    campaignReport,
    userData,
    avgEngagement,
    totalEngagement,
    cpEValue,
    cpmValue,
    isVisible,
    onCustomizeClick,
    is_public,
    shareKey,
    campaignId,
    orgId,
    brandId,
    isReportGenerated = true, // Default to true to maintain backward capability if not passed
    onDataLoaded,
    campaignDetail,
    refreshTrigger = 0,
    campaignPlatform,
    reportType,
    influencerList = [],
    isSharedView,
    isInitialLoading = false,
}) => {
    const [localContentAnalytic, setLocalContentAnalytic] = useState(null);
    const [localReport, setLocalReport] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    // Platform Check
    // Check various common platform identifiers from campaign settings
    const platformId = campaignDetail?.campaign_data?.platform_data?.platform_id ||
        campaignDetail?.campaign_data?.platform_id;

    // Also check actual content types from influencer list data
    // Instagram content types: fxt1, ge03, utio, uyts, o5oi
    // YouTube content types: ttsn, vi82
    const instagramContentTypes = ['fxt1', 'ge03', 'utio', 'uyts', 'o5oi'];
    const youtubeContentTypes = ['ttsn', 'vi82'];

    let hasInstagramContent = false;
    let hasYoutubeContent = false;

    if (influencerList && influencerList.length > 0) {
        influencerList.forEach(item => {
            const contentAnalytics = item?.content_analytics || {};
            Object.keys(contentAnalytics).forEach(key => {
                const contentTypeId = contentAnalytics[key]?.content_type_id;
                if (instagramContentTypes.includes(contentTypeId)) {
                    hasInstagramContent = true;
                } else if (youtubeContentTypes.includes(contentTypeId)) {
                    hasYoutubeContent = true;
                }
            });
        });
    }

    // Determine platform: prioritize actual content data over campaign settings
    const isInstagram = hasInstagramContent ||
        (!hasYoutubeContent && (
            platformId === 1 ||
            platformId === 'btll' ||
            platformId === 'instagram' ||
            campaignPlatform?.toLowerCase() === 'instagram'
        ));

    // Label for views/subscribers metric
    const subscriberLabel = isInstagram ? "Views/Follower Ratio" : "Views/Subscriber Ratio";

    useEffect(() => {
        // If props are already provided, mark as loaded
        if (campaignContentAnalytic && campaignReport && refreshTrigger === 0) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);

        // Fetch if props are missing
        if (is_public && shareKey) {
            const params = { share_key: shareKey };
            let analyticsLoaded = false;
            let reportLoaded = false;

            const checkDone = () => {
                if (analyticsLoaded && reportLoaded) setIsLoading(false);
            };

            Core.getPublicCampaignContentsAnalytics(
                (response) => {
                    const data = response?.response || response?.campaign_content_analytics_data || response;
                    setLocalContentAnalytic(data);
                    analyticsLoaded = true;
                    checkDone();
                },
                (err) => { console.error(err); analyticsLoaded = true; checkDone(); },
                () => { },
                params
            );
            Core.getCampaignReportPublic(
                (response) => {
                    const data = response?.response || response;
                    setLocalReport(data);
                    reportLoaded = true;
                    checkDone();
                },
                (err) => { reportLoaded = true; checkDone(); },
                () => { },
                params
            );
        } else if (!is_public && campaignId) {
            // Private fetch
            const params = { campaign_id: campaignId };
            Core.getCampaignStatistics(
                (response) => {
                    const data = response?.response || response;
                    setLocalContentAnalytic(data);
                    setLocalReport(data);
                    setIsLoading(false);

                    // Bubble up data and report_id to parent
                    if (onDataLoaded) {
                        onDataLoaded({
                            report_id: data?.report_id,
                            analytics_data: data
                        });
                    }
                },
                (err) => { console.error(err); setIsLoading(false); },
                () => { },
                params
            );
        } else {
            // No source available, stop loading
            setIsLoading(false);
        }
    }, [campaignContentAnalytic, campaignReport, is_public, shareKey, campaignId, refreshTrigger]);

    const aggregatedData = useMemo(() => {
        if (!influencerList || influencerList.length === 0) return null;

        const result = {
            view_count: 0,
            likes: 0,
            comments: 0,
            impressions: 0,
            reach: 0,
            share: 0,
            saves: 0,
            replies: 0,
            sticker_taps: 0,
            conversions: 0,
            total_clicks: 0,
            submissions: 0,
            remixes: 0,
            installs: 0,
            registration: 0,
            deposit: 0,
            planned_views: 0,
            video_likes: 0,
            video_comments: 0,
            static_likes: 0,
            static_comments: 0,
            total_spent_completed: 0,
            total_spent_client_completed: 0,

            // New derived metrics
            total_followers: 0,
            sum_engagement_rate: 0,
            influencer_count_for_avg: 0
        };

        influencerList.forEach(item => {
            const contentAnalytics = item?.content_analytics || {};
            const contentsDeepData = item?.contents_deep_data || {};

            // Reach = Sum of followers
            const platforms = item?.platforms_data || [];
            let influencerFollowers = 0;
            if (Array.isArray(platforms)) {
                platforms.forEach(p => {
                    influencerFollowers += Number(p.followers || 0);
                });
            } else if (platforms && typeof platforms === 'object') {
                if (platforms.followers) {
                    influencerFollowers = Number(platforms.followers);
                } else {
                    Object.values(platforms).forEach(p => {
                        influencerFollowers += Number(p?.followers || 0);
                    });
                }
            }
            result.total_followers += influencerFollowers;

            // Profile Engagement = Average of engagement_rate
            // We sum them up here and divide by count later
            // Use analytics.engagement_rate from the assignment/influencer data if available
            // Check where engagement_rate is stored usually in assignment object
            const pData = item?.platforms_data;
            let engRate = 0;
            if (pData) {
                if (Array.isArray(pData)) {
                    engRate = Number(pData[0]?.engagement_rate || 0);
                } else {
                    engRate = Number(pData?.engagement_rate || 0);
                }
            }

            if (engRate === 0) {
                engRate = Number(item?.analytics?.engagement_rate || item?.engagement_rate || 0);
            }
            if (engRate > 0) {
                result.sum_engagement_rate += engRate;
                result.influencer_count_for_avg += 1;
            }

            // Track completed spend per influencer
            // Assuming reward_data.value.amount is the confirmed/completed amount
            const itemPaid = Number(item?.reward_data?.value?.amount) || 0;
            const itemPlanned = Number(item?.assignment_data?.reward_value_client || item?.reward_value_client) || 0;

            // For social media campaigns, we need to add product_price from content_analytics
            // to the total spent for accurate CPE/CPV calculations
            let totalProductPrice = 0;

            // Collect all unique content keys
            const contentKeys = new Set([
                ...Object.keys(contentAnalytics),
                ...Object.keys(contentsDeepData)
            ]);

            contentKeys.forEach(key => {
                const analytics = contentAnalytics[key] || {};
                const deep = contentsDeepData[key] || {};

                // Add product_price from each content
                const productPrice = Number(analytics.product_price || 0);
                totalProductPrice += productPrice;

                const views = Number(analytics.view_count || analytics.views || 0);
                const likes = Number(analytics.like_count || 0);
                const comments = Number(analytics.comment_count || 0);
                const impressions = Number(analytics.impressions || 0);
                // metrics.reach is content reach.
                // User requirement: "Reach" column in table is followers.
                // But generally "Reach" in overview means Content Reach or Account Reach?
                // Defaulting "Reach" card to Content Reach sum if available, OR Followers if requested.
                // Re-reading: "Reach: sum of platforms_data.followers".
                // So for Overview Card "Reach", we should use total_followers.
                const contentReach = Number(analytics.reach || 0);

                const share = Number(analytics.share || 0);
                const saves = Number(analytics.saves || 0);
                const replies = Number(analytics.replies || 0);
                const sticker = Number(analytics.sticker_taps || 0);
                const conversions = Number(analytics.conversions || 0);
                const clicks = Number(analytics.total_clicks || 0);

                // Add to totals
                result.view_count += views;
                result.likes += likes;
                result.comments += comments;
                result.impressions += impressions;
                // Accumulate content reach still, just in case
                result.reach += contentReach;
                result.share += share;
                result.saves += saves;
                result.replies += replies;
                result.sticker_taps += sticker;
                result.conversions += conversions;
                result.total_clicks += clicks;

                // Handle other potential metrics
                result.submissions += Number(analytics.submissions || 0);
                result.remixes += Number(analytics.remixes || 0);
                result.installs += Number(analytics.installs || 0);
                result.registration += Number(analytics.registration || 0);
                result.deposit += Number(analytics.deposit || 0);
                result.planned_views += Number(analytics.planned_views || 0);

                // For breakdown-based E.R.
                const contentType = analytics.content_type_id || deep?.content_data?.content_type_id;
                const isVideo = ['ge03', 'uyts', 'o5oi', 'ttsn', 'vi82'].includes(contentType);

                if (isVideo) {
                    result.video_likes += likes;
                    result.video_comments += comments;
                } else {
                    result.static_likes += likes;
                    result.static_comments += comments;
                }
            });

            // Add the reward + product_price to total spent
            result.total_spent_completed += (itemPaid + totalProductPrice);
            result.total_spent_client_completed += itemPlanned;
        });

        // Add aggregate engagement total (Likes + Comments + Shares + Saves)
        result.total_engagements = result.likes + result.comments + result.share + result.saves;

        return result;
    }, [influencerList]);

    // MERGE LOGIC: Combine aggregated data from influencer list with existing analytics
    // This allows for real-time frontend updates when individual influencer metrics change
    const effectiveAnalytic = useMemo(() => {
        const base = campaignContentAnalytic || localContentAnalytic || {};
        if (!aggregatedData) return base;

        // If we have aggregated data from the influencer list, it becomes the source of truth
        // for the fields it calculates, while preserving other fields from the API (like planned_cpv)
        return {
            ...base,
            ...aggregatedData,
            // Specifically ensure we use the aggregated totals
            total_play_count: aggregatedData.view_count || base.total_play_count,
            total_like_count: aggregatedData.likes || base.total_like_count,
            total_comment_count: aggregatedData.comments || base.total_comment_count,
            total_eng_rate: aggregatedData.total_engagements || base.total_eng_rate,
            // Reach from followers sum
            reach: aggregatedData.total_followers > 0 ? aggregatedData.total_followers : (aggregatedData.reach || base.reach),
            // Profile Engagement calculated below
        };
    }, [aggregatedData, campaignContentAnalytic, localContentAnalytic]);

    // For report data (money), we prefer the aggregate calculations if we derived them from the list
    const effectiveReport = (aggregatedData && !is_public)
        ? {
            ...localReport,
            total_spent_completed: aggregatedData.total_spent_completed,
            total_spent_client_completed: aggregatedData.total_spent_client_completed,
            // Fallbacks for display
            total_spent: aggregatedData.total_spent_completed,
            total_spent_client: aggregatedData.total_spent_client_completed,
            // Pass total_followers to report reach if needed, though analytic reach is priority
            total_reach: aggregatedData.total_followers > 0 ? aggregatedData.total_followers : localReport?.total_reach
        }
        : (campaignReport || localReport);

    // Calculate metrics if not provided via props
    const calculateMetrics = () => {
        if (avgEngagement !== undefined && totalEngagement !== undefined) {
            return {
                finalAvg: avgEngagement,
                finalTotal: totalEngagement,
                finalCpe: cpEValue,
                finalCpm: cpmValue,
                finalLikes: 0,
                finalComments: 0,
                finalVideoEngRate: 0,
                finalStaticEngRate: 0,
                finalProfileEngRate: 0
            };
        }

        // Check if breakdown stats exist in the response
        const hasBreakdown = [
            effectiveAnalytic?.video_likes,
            effectiveAnalytic?.static_likes,
            effectiveAnalytic?.video_comments,
            effectiveAnalytic?.static_comments
        ].some(v => v !== undefined && v !== null);

        const videoLikes = Number(effectiveAnalytic?.video_likes) || 0;
        const staticLikes = Number(effectiveAnalytic?.static_likes) || 0;
        const videoComments = Number(effectiveAnalytic?.video_comments) || 0;
        const staticComments = Number(effectiveAnalytic?.static_comments) || 0;

        const views = Number(effectiveAnalytic?.view_count || effectiveAnalytic?.views || effectiveAnalytic?.total_play_count) || 0;
        const shares = Number(effectiveAnalytic?.share || 0);
        const saves = Number(effectiveAnalytic?.saves || 0);
        const reach = Number(effectiveAnalytic?.reach || effectiveReport?.total_reach) || 0;

        // Determine Total Spent for display (used in other calculations like Total Spent card)
        let totalSpent = 0;
        if (is_public) {
            // Public Page Logic
            // In public page, only show the Client Report price for inhouse and total_spent for marketplace
            const isMarketplace = campaignDetail?.campaign_data?.is_marketplace === 1;
            if (isMarketplace) {
                totalSpent = Number(effectiveReport?.total_spent) || 0;
            } else {
                // Inhouse -> Client Price
                totalSpent = Number(effectiveReport?.total_spent_client) || Number(effectiveReport?.total_spent) || 0;
            }
        } else {
            // Private Page Logic
            // Toggle Self/Client
            if (reportType === 'client') {
                totalSpent = Number(effectiveReport?.total_spent_client) || 0;
            } else {
                totalSpent = Number(effectiveReport?.total_spent) || 0;
            }
        }

        // Determine Completed Spent for CPE/CPV calculations
        let completedSpent = 0;
        if (is_public) {
            const isMarketplace = campaignDetail?.campaign_data?.is_marketplace === 1;
            if (isMarketplace) {
                completedSpent = Number(effectiveReport?.total_spent_completed) || 0;
            } else {
                completedSpent = Number(effectiveReport?.total_spent_client_completed) || Number(effectiveReport?.total_spent_completed) || 0;
            }
        } else {
            if (reportType === 'client') {
                completedSpent = Number(effectiveReport?.total_spent_client_completed) || 0;
            } else {
                completedSpent = Number(effectiveReport?.total_spent_completed) || 0;
            }
        }
        // When using aggregatedData, product_price is already included in total_spent_completed
        // So we should NOT add completedProduct again to avoid double-counting
        const completedProduct = Number(effectiveReport?.total_product_price_completed) || 0;
        const totalCompletedSpent = (aggregatedData && !is_public)
            ? completedSpent  // Product price already included in aggregatedData.total_spent_completed
            : completedSpent + completedProduct;  // Add product price for non-aggregated data

        let likes, comments, total;

        if (hasBreakdown) {
            // Total Engagement = likes + comments + shares + saves
            likes = videoLikes + staticLikes;
            comments = videoComments + staticComments;
            total = likes + comments + shares + saves;
        } else {
            // Fallback to legacy/aggregate fields if breakdown is missing
            likes = Number(effectiveAnalytic?.likes || effectiveAnalytic?.total_like_count) || 0;
            comments = Number(effectiveAnalytic?.comments || effectiveAnalytic?.total_comment_count) || 0;
            total = likes + comments + shares + saves;
        }

        // Custom Formulas Requested
        // E.R. (Video) = ((video_likes + video_comments)/views)*100
        // E.R. (Static) = ((static_likes + static_comments)/reach)*100
        const videoEngRate = views > 0 ? ((videoLikes + videoComments) / views) * 100 : 0;
        const staticEngRate = reach > 0 ? ((staticLikes + staticComments) / reach) * 100 : 0;

        // E.R. = ((like_count + comment_count + share + saves) / view_count) * 100
        const avg = views > 0 ? ((likes + comments + shares + saves) / views) * 100 : 0;

        // Calculate CPE/CPV using the standard formulas
        // Prioritize manual calculation from totals to stay in sync with table data
        const cpe = total > 0 ? (totalCompletedSpent / total) : 0;
        const cpm = views > 0 ? (totalCompletedSpent / views) : 0;

        // Profile Engagement = (Sum of Influencer E.R.) / (Number of influencers)
        const aggregatedProfileEng = aggregatedData
            ? (aggregatedData.influencer_count_for_avg > 0 ? (aggregatedData.sum_engagement_rate / aggregatedData.influencer_count_for_avg) : 0)
            : undefined;

        const profileEngRate = aggregatedProfileEng !== undefined
            ? aggregatedProfileEng
            : (effectiveReport?.avg_engagement_rate !== undefined
                ? Number(effectiveReport?.avg_engagement_rate)
                : (effectiveAnalytic?.profile_eng_rate !== undefined ? Number(effectiveAnalytic.profile_eng_rate) : 0));

        // Views/Subscriber (or Views/Follower) Ratio = Views / Reach
        const viewsSubscriberRatio = reach > 0 ? (views / reach).toFixed(2) : 0;

        return {
            finalAvg: avg,
            finalTotal: total,
            finalCpe: cpe,
            finalCpm: cpm,
            finalVideoEngRate: videoEngRate,
            finalStaticEngRate: staticEngRate,
            finalLikes: likes,
            finalComments: comments,
            finalProfileEngRate: profileEngRate,
            finalViewsSubscriberRatio: viewsSubscriberRatio
        };
    };

    const { finalAvg, finalTotal, finalCpe, finalCpm, finalVideoEngRate, finalStaticEngRate, finalLikes, finalComments, finalProfileEngRate, finalViewsSubscriberRatio } = calculateMetrics();

    // Helper to format value based on report status
    const formatValue = (value) => {
        // Check report_status from API response
        if (isReportGenerated === false) return "-";

        // Handle 0 values as requested
        if (!value) return "-";

        // Check for numeric 0 or less
        if (typeof value === 'number' && value <= 0) return "-";

        // Convert to string to check for various 0 formats
        const strVal = String(value).trim();

        if (strVal === "0" || strVal === "0%" || strVal === "₹0") return "-";
        if (strVal === "0.00" || strVal === "0.00%" || strVal === "₹0.00") return "-";
        if (strVal === "0.000" || strVal === "0.000%") return "-"; // Handle 3 decimals

        // Check if string parses to 0 (careful strictly matching 0 formatting)
        if (!isNaN(parseFloat(strVal)) && parseFloat(strVal) <= 0) return "-";

        return value;
    };

    const formatCount = (value) => {
        if (isReportGenerated === false) return "-";
        const num = Number(value) || 0;
        if (num <= 0) return "-";
        return Utils.changeNumberFormate(Math.round(num));
    };


    return (
        <div className="learning-tab section-content" ref={sectionRef}>
            <div className="section-header">
                <h3>Statistics Overview</h3>
                {!is_public && !isSharedView && isVisible !== undefined && (
                    <div className="section-controls">
                        {/* <div
                            className="customize-btn"
                            onClick={onCustomizeClick}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                cursor: 'pointer',
                                color: '#1977f2', // var(--blue-blue-500)
                                fontSize: '14px',
                                fontWeight: 500,
                            }}
                        >
                            <EditIconBlue />
                            <span>Customize</span>
                        </div> */}
                        <div className='sharing-status-container'>
                            <span className="sharing-status">Sharing status</span>
                            {isVisible ? (
                                <span className="visible-badge">Visible</span>
                            ) : (
                                <span className="hidden-badge">Hidden</span>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {(isInitialLoading || isLoading) ? (
                <ShimmerLoading />
            ) : (
            <div className="metrics-grid">
                {/* Row 1 */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Views"
                    value={formatCount(effectiveAnalytic?.view_count || effectiveAnalytic?.views || effectiveAnalytic?.total_play_count)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Likes"
                    value={formatCount(finalLikes)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Comments"
                    value={formatCount(finalComments)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="E.R. (Avg.)"
                    value={formatValue(`${Number.isFinite(finalAvg) && finalAvg > 0 ? finalAvg.toFixed(2) : "0"}%`)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="E.R. (Video)"
                    value={formatValue(`${Number.isFinite(finalVideoEngRate) && finalVideoEngRate > 0 ? finalVideoEngRate.toFixed(2) : "0"}%`)}
                // badge="New"
                />

                {/* Row 2 */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="E.R. (Static)"
                    value={formatValue(`${Number.isFinite(finalStaticEngRate) && finalStaticEngRate > 0 ? finalStaticEngRate.toFixed(2) : "0"}%`)}
                // badge="New"
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Total Engagements"
                    value={formatCount(finalTotal)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="CPE"
                    value={formatValue(finalCpe < Infinity && finalCpe !== 0
                        ? Utils.convertFromINR(finalCpe, userData?.country)
                        : "₹0")}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="CPV"
                    value={formatValue(finalCpm < Infinity && finalCpm > 0
                        ? Utils.convertFromINR(finalCpm, userData?.country)
                        : "₹0")}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Impressions"
                    value={formatCount(effectiveAnalytic?.impressions)}
                />

                {/* Row 3 */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Reach"
                    value={formatCount(effectiveAnalytic?.reach || effectiveReport?.total_reach)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Share"
                    value={formatCount(effectiveAnalytic?.share)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Saves"
                    value={formatCount(effectiveAnalytic?.saves)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label={subscriberLabel}
                    value={formatValue(finalViewsSubscriberRatio || "0")}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Profile Engagement"
                    value={formatValue(`${finalProfileEngRate ? finalProfileEngRate.toFixed(2) : "0"}%`)}
                />

                {/* Row 4 */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Replies"
                    value={formatCount(effectiveAnalytic?.replies)}
                />
                {/* <RenderMetricCard
                    isPublic={is_public}
                    label="Submissions"
                    value={formatCount(effectiveAnalytic?.submissions)}
                /> */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Remixes"
                    value={formatCount(effectiveAnalytic?.remixes)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Conversions"
                    value={formatCount(effectiveAnalytic?.conversions)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Sticker Taps"
                    value={formatCount(effectiveAnalytic?.sticker_taps)}
                />

                {/* Row 5 */}
                {/* <RenderMetricCard
                    isPublic={is_public}
                    label="Planned CPV"
                    value={formatValue(effectiveAnalytic?.planned_cpv
                        ? Utils.convertFromINR(effectiveAnalytic.planned_cpv, userData?.country)
                        : "₹0")}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    label="Planned Views"
                    value={formatCount(effectiveAnalytic?.planned_views)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    label="Installs"
                    value={formatCount(effectiveAnalytic?.installs)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    label="Registration"
                    value={formatCount(effectiveAnalytic?.registration)}
                />
                <RenderMetricCard
                    isPublic={is_public}
                    label="Deposit"
                    value={formatCount(effectiveAnalytic?.deposit)}
                /> */}

                {/* Row 6 */}
                <RenderMetricCard
                    isPublic={is_public}
                    isSharedView={isSharedView}
                    label="Total Clicks"
                    value={formatCount(effectiveAnalytic?.total_clicks)}
                />
            </div>
            )}
        </div>
    );
};

export default OverviewSection;
